import { useState } from "react";
import {
  Table, Button, Input, Select, Tag, Card, Modal, Form,
  Popconfirm, Space, message, Switch, InputNumber,
} from "antd";
import { ArrowLeft, Plus, Search, Pencil, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useClientList, useClientCreate, useClientUpdate, useClientDelete } from "../../../api-hooks/clients";

const CLIENT_TYPE_COLORS: Record<string, string> = {
  commercial: "blue",
  residential: "green",
  public_sector: "purple",
};

export default function ClientsPage() {
  const { companyId: cParam } = useParams<{ companyId: string }>();
  const cid = Number(cParam);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const { data: result, isLoading } = useClientList(cid, {
    search: search || undefined,
    client_type: typeFilter || undefined,
    page,
    limit: PAGE_SIZE,
  });
  const clients = result?.data ?? [];
  const total = result?.total ?? 0;

  const [modal, setModal] = useState<{ open: boolean; record: any | null }>({ open: false, record: null });
  const [form] = Form.useForm();

  const create = useClientCreate(cid, {
    onSuccess: () => { msgApi.success("Client created."); closeModal(); },
    onError: (e: any) => msgApi.error(e?.message || "Failed."),
  });
  const update = useClientUpdate(cid, {
    onSuccess: () => { msgApi.success("Client updated."); closeModal(); },
    onError: (e: any) => msgApi.error(e?.message || "Failed."),
  });
  const del = useClientDelete(cid, {
    onSuccess: () => msgApi.success("Client deleted."),
    onError: () => msgApi.error("Failed to delete."),
  });

  function openCreate() { setModal({ open: true, record: null }); form.resetFields(); form.setFieldValue("client_type", "commercial"); form.setFieldValue("payment_terms_days", 30); }
  function openEdit(r: any) { setModal({ open: true, record: r }); form.setFieldsValue({ ...r }); }
  function closeModal() { setModal({ open: false, record: null }); form.resetFields(); }

  function onFinish(vals: any) {
    if (modal.record) update.mutate({ ...vals, id: modal.record.id });
    else create.mutate(vals);
  }

  const columns = [
    {
      title: "Client",
      key: "name",
      render: (_: any, r: any) => (
        <div>
          <div className="font-medium text-slate-800">
            {r.business_name || `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() || "—"}
          </div>
          <div className="text-xs text-slate-500">{r.email ?? "—"}</div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "client_type",
      key: "client_type",
      render: (v: string) => <Tag color={CLIENT_TYPE_COLORS[v] || "default"}>{v?.replace("_", " ").toUpperCase()}</Tag>,
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, r: any) => (
        <div className="text-xs text-slate-600">
          {r.phone ? <div>{r.phone}</div> : null}
          {r.city ? <div>{[r.city, r.postcode].filter(Boolean).join(", ")}</div> : null}
        </div>
      ),
    },
    {
      title: "Account Ref",
      dataIndex: "account_reference",
      key: "account_reference",
      render: (v: string) => v ? <span className="font-mono text-xs">{v}</span> : <span className="text-slate-400">—</span>,
    },
    {
      title: "Payment Terms",
      dataIndex: "payment_terms_days",
      key: "payment_terms_days",
      render: (v: number) => `${v} days`,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (v: any) => <Tag color={v ? "green" : "red"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "",
      key: "actions",
      render: (_: any, r: any) => (
        <Space size={4}>
          <Button size="small" icon={<Pencil size={13} />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this client?" onConfirm={() => del.mutate(r.id)}>
            <Button size="small" danger icon={<Trash2 size={13} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      {ctx}
      <div className="flex items-center gap-3 mb-6">
        <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} />
        <div>
          <h1 className="text-xl font-semibold text-slate-800 mb-0">Clients</h1>
          <p className="text-slate-500 text-sm">Company ID: {cid} — {total} total</p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={openCreate}
          className="ml-auto"
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
        >
          Add Client
        </Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <div className="flex gap-3">
          <Input
            prefix={<Search size={14} className="text-slate-400" />}
            placeholder="Search name, email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ maxWidth: 300 }}
            allowClear
          />
          <Select
            placeholder="Client type"
            value={typeFilter || undefined}
            onChange={(v) => { setTypeFilter(v || ""); setPage(1); }}
            allowClear
            style={{ width: 160 }}
            options={[
              { value: "commercial", label: "Commercial" },
              { value: "residential", label: "Residential" },
              { value: "public_sector", label: "Public Sector" },
            ]}
          />
        </div>
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={clients}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            showTotal: (t) => `${t} clients`,
          }}
          scroll={{ x: "max-content" }}
          size="small"
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={modal.record ? "Edit Client" : "Add Client"}
        open={modal.open}
        onCancel={closeModal}
        footer={null}
        width={680}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="client_type" label="Client Type" rules={[{ required: true }]}>
              <Select options={[
                { value: "commercial", label: "Commercial" },
                { value: "residential", label: "Residential" },
                { value: "public_sector", label: "Public Sector" },
              ]} />
            </Form.Item>
            <Form.Item name="business_name" label="Business Name">
              <Input />
            </Form.Item>
            <Form.Item name="first_name" label="First Name">
              <Input />
            </Form.Item>
            <Form.Item name="last_name" label="Last Name">
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input type="email" />
            </Form.Item>
            <Form.Item name="phone" label="Phone">
              <Input />
            </Form.Item>
            <Form.Item name="mobile" label="Mobile">
              <Input />
            </Form.Item>
            <Form.Item name="account_reference" label="Account Reference">
              <Input />
            </Form.Item>
          </div>

          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">Address</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="address_line1" label="Address Line 1" className="col-span-2">
              <Input />
            </Form.Item>
            <Form.Item name="address_line2" label="Address Line 2" className="col-span-2">
              <Input />
            </Form.Item>
            <Form.Item name="city" label="City">
              <Input />
            </Form.Item>
            <Form.Item name="county" label="County">
              <Input />
            </Form.Item>
            <Form.Item name="postcode" label="Postcode">
              <Input />
            </Form.Item>
            <Form.Item name="country" label="Country" initialValue="United Kingdom">
              <Input />
            </Form.Item>
          </div>

          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3 mt-1">Financial</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="payment_terms_days" label="Payment Terms (days)" initialValue={30}>
              <InputNumber min={0} max={365} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="credit_limit" label="Credit Limit (£)">
              <InputNumber min={0} step={100} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          {modal.record && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <div className="flex justify-end gap-2">
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={create.isPending || update.isPending}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              {modal.record ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
